// frontend/src/components/admin/AdminOTPVerification.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';
import { FaArrowLeft, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import './AdminOTPVerification.css';

const AdminOTPVerification = () => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const [email, setEmail] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from location state or localStorage
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem('adminLoginEmail');
    
    const finalEmail = emailFromState || emailFromStorage;
    
    if (finalEmail) {
      setEmail(finalEmail);
      localStorage.setItem('adminLoginEmail', finalEmail);
    } else {
      // No email found, redirect to login
      navigate('/admin/login');
      return;
    }

    // Start countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, location]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/admin/verify-login-otp', {
        email: email,
        otp: otp.trim()
      });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminData', JSON.stringify(response.data.admin));
        localStorage.removeItem('adminLoginEmail');
        
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        navigate('/admin/dashboard/home');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.error || 'Failed to verify OTP. Please try again.');
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');

    try {
      await axiosInstance.post('/admin/resend-login-otp', { email });
      
      setTimeLeft(300);
      setCanResend(false);
      setError('');
      
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="otp-verification-container">
      <div className="otp-verification-card">
        <button 
          className="otp-back-btn"
          onClick={() => {
            localStorage.removeItem('adminLoginEmail');
            navigate('/admin/login');
          }}
        >
          <FaArrowLeft /> Back to Login
        </button>

        <div className="otp-header">
          <div className="otp-logo-circle">
            <FaExclamationCircle size={24} />
          </div>
          <h1>Verify OTP</h1>
          <p className="otp-subtitle">
            Enter the 6-digit OTP sent to <strong>{email}</strong>
          </p>
        </div>

        {error && (
          <div className="otp-error-message">
            <FaExclamationCircle /> {error}
          </div>
        )}

        <form onSubmit={handleVerifyOTP} className="otp-form">
          <div className="otp-input-group">
            <input
              type="text"
              maxLength="6"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              className="otp-input"
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="otp-timer-section">
            {timeLeft > 0 ? (
              <span className="otp-timer">
                ⏱️ OTP expires in: <strong>{formatTime(timeLeft)}</strong>
              </span>
            ) : (
              <span className="otp-expired">OTP expired</span>
            )}
          </div>

          <button
            type="submit"
            className="otp-verify-btn"
            disabled={loading || otp.length !== 6}
          >
            {loading ? (
              <>
                <FaSpinner className="spin" /> Verifying...
              </>
            ) : (
              'Verify OTP'
            )}
          </button>

          <button
            type="button"
            className="otp-resend-btn"
            onClick={handleResendOTP}
            disabled={loading || !canResend}
          >
            {canResend ? 'Resend OTP' : `Resend available in ${formatTime(timeLeft)}`}
          </button>
        </form>

        <div className="otp-note">
          <p>OTP sent to your registered email address.</p>
          <p className="otp-note-small">Valid for 5 minutes only.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminOTPVerification;